import { Outlet, createFileRoute, redirect, useRouterState } from "@tanstack/react-router";
import { Link } from "@tanstack/react-router";
import { logout } from "../lib/auth";
import { ApiError } from "../lib/api";
import { ToastHost } from "../components/shared/toast";

import { me, type CurrentUser } from "../lib/auth";

export const Route = createFileRoute("/_authed")({
  beforeLoad: async ({ context }) => {
    if ((context as { user?: unknown }).user) return;
    try {
      const user = await me();
      return { user };
    } catch (e) {
      // Só desloga quando a API diz que a sessão não vale (401/403).
      // Timeout/504/erro de rede NÃO é logout — propaga pro ErrorBoundary,
      // senão qualquer soluço de infra vira "logout fantasma".
      if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
        throw redirect({ to: "/login" });
      }
      throw e;
    }
  },
  component: Layout,
});

// ── SVG icons ──────────────────────────────────────────────────────────────
function IconDashboard() {
  return (
    <svg className="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" style={{ width: 18, height: 18, flexShrink: 0 }}>
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <rect x="14" y="14" width="7" height="7" rx="1" />
    </svg>
  );
}

function IconAgentes() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" style={{ width: 18, height: 18, flexShrink: 0 }}>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
      <circle cx="18.5" cy="7.5" r="2" />
      <path d="M21 12c1 .5 2 1.5 2 3" />
    </svg>
  );
}


function IconPainelDev() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" style={{ width: 18, height: 18, flexShrink: 0 }}>
      <polyline points="16 18 22 12 16 6" />
      <polyline points="8 6 2 12 8 18" />
    </svg>
  );
}

function IconEstoque() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" style={{ width: 18, height: 18, flexShrink: 0 }}>
      <rect x="1" y="13" width="22" height="6" rx="2" />
      <path d="M4 13V9a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v4" />
      <circle cx="7" cy="19" r="2" />
      <circle cx="17" cy="19" r="2" />
    </svg>
  );
}

export const PATIOO_PERMS = ["patioo_acesso", "patioo_criar", "patioo_participantes", "patioo_alterar_proprietario", "patioo_excluir"] as const;

function IconPatioo() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" style={{ width: 18, height: 18, flexShrink: 0 }}>
      <rect x="3" y="4" width="18" height="16" rx="3" />
      <path d="M3 9h18" />
      <path d="M8 13h8M8 16h5" />
    </svg>
  );
}

function IconSelfService() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" style={{ width: 18, height: 18, flexShrink: 0 }}>
      <rect x="3" y="4" width="18" height="14" rx="2" />
      <path d="M7 9h4M7 13h10M12 18v3M8 21h8" />
    </svg>
  );
}

function IconWaba() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: 18, height: 18, flexShrink: 0 }}>
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
    </svg>
  );
}

function IconRelatorios() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" style={{ width: 18, height: 18, flexShrink: 0 }}>
      <path d="M3 3v18h18" />
      <rect x="7" y="12" width="3" height="6" rx="0.5" />
      <rect x="12" y="8" width="3" height="10" rx="0.5" />
      <rect x="17" y="5" width="3" height="13" rx="0.5" />
    </svg>
  );
}

function IconFinanceiro() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" style={{ width: 18, height: 18, flexShrink: 0 }}>
      <rect x="2" y="6" width="20" height="13" rx="2" />
      <circle cx="12" cy="12.5" r="2.5" />
      <path d="M6 10v.01M18 15v.01" />
    </svg>
  );
}

function IconWiki() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" style={{ width: 18, height: 18, flexShrink: 0 }}>
      <path d="M2 4h6a3 3 0 0 1 3 3v13a2.5 2.5 0 0 0-2.5-2.5H2z" />
      <path d="M22 4h-6a3 3 0 0 0-3 3v13a2.5 2.5 0 0 1 2.5-2.5H22z" />
    </svg>
  );
}

function IconTarefas() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" style={{ width: 18, height: 18, flexShrink: 0 }}>
      <rect x="3" y="4" width="18" height="17" rx="2" />
      <path d="M8 2v4M16 2v4M7.5 13l3 3 6-6" />
    </svg>
  );
}

function IconSettings() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" style={{ width: 18, height: 18, flexShrink: 0 }}>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 2v2M12 20v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M2 12h2M20 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
    </svg>
  );
}

function IconUser() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <circle cx="12" cy="8" r="4" />
      <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
    </svg>
  );
}

// ── Nav item ───────────────────────────────────────────────────────────────
function NavItem({
  to,
  icon,
  label,
  active,
}: {
  to: string;
  icon: React.ReactNode;
  label: string;
  active: boolean;
}) {
  const base = "flex items-center gap-[10px] px-3 py-[9px] rounded-[8px] text-sm w-full text-left transition-colors duration-100";
  const inactive = "text-[var(--ink-muted)] hover:bg-[var(--surface-hover)] hover:text-[var(--ink)]";
  const activeClass = "bg-[var(--brand-soft)] text-[var(--brand)] font-medium";

  return (
    <Link to={to} className={`${base} ${active ? activeClass : inactive}`}>
      <span style={{ opacity: active ? 1 : 0.7 }}>{icon}</span>
      {label}
    </Link>
  );
}

// ── Layout ─────────────────────────────────────────────────────────────────
function Layout() {
  const context = Route.useRouteContext();
  const user = (context as { user?: CurrentUser }).user;
  const isViewer = !!user?.permissions?.viewer;
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  function isActive(prefix: string) {
    if (prefix === "/") return pathname === "/";
    return pathname.startsWith(prefix);
  }

  return (
    <div style={{ display: "flex", height: "100vh", overflow: "hidden", background: "var(--surface-app)" }}>
      {/* ── Sidebar ── */}
      <aside
        style={{
          width: 220,
          background: "var(--surface-sidebar)",
          borderRight: "1px solid var(--line)",
          display: "flex",
          flexDirection: "column",
          flexShrink: 0,
        }}
      >
        {/* Logo — placa de marca escura (a arte do logo é clara) */}
        <div style={{ padding: "14px 12px", borderBottom: "1px solid var(--line)" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 8,
              background: "var(--logo-plate)",
              borderRadius: 8,
              padding: "10px 12px",
            }}
          >
            <img
              src="/logo-carmillion.avif"
              alt="Carmillion"
              style={{ height: 22, width: "auto", objectFit: "contain" }}
            />
            <span
              style={{
                fontFamily: "var(--font-display)",
                fontSize: 9,
                fontWeight: 700,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: "var(--brand-accent)",
                background: "rgba(225,72,72,0.12)",
                border: "1px solid rgba(225,72,72,0.38)",
                borderRadius: 4,
                padding: "2px 6px",
                flexShrink: 0,
              }}
            >
              Valquiria
            </span>
          </div>
        </div>

        {/* Nav */}
        <nav style={{ flex: 1, padding: "12px 8px", display: "flex", flexDirection: "column", gap: 2 }}>
          <NavItem to="/" icon={<IconDashboard />} label="Dashboard" active={isActive("/")} />
          <NavItem to="/agents" icon={<IconAgentes />} label="Agentes" active={isActive("/agents")} />
          <NavItem to="/tarefas" icon={<IconTarefas />} label="Tarefas" active={isActive("/tarefas")} />
          {!isViewer && <NavItem to="/estoque" icon={<IconEstoque />} label="APIs Estoque" active={isActive("/estoque")} />}
          {(user?.role_name === "Admin" || PATIOO_PERMS.some(p => !!user?.permissions?.[p])) && (
            <NavItem to="/patioo" icon={<IconPatioo />} label="Patioo" active={isActive("/patioo")} />
          )}
          {!isViewer && <NavItem to="/waba" icon={<IconWaba />} label="WABA" active={isActive("/waba")} />}
          {user?.role_name === "Admin" && (
            <NavItem to="/self-service" icon={<IconSelfService />} label="Self-service" active={isActive("/self-service")} />
          )}
          {(user?.role_name === "Admin" || isViewer || !!user?.permissions?.relatorios) && (
            <NavItem to="/reports/managers" icon={<IconRelatorios />} label="Relatórios" active={isActive("/reports")} />
          )}
          {(user?.role_name === "Admin" || !!user?.permissions?.financeiro) && (
            <NavItem to="/financeiro" icon={<IconFinanceiro />} label="Financeiro" active={isActive("/financeiro")} />
          )}
          {(user?.role_name === "Admin" || (["dev_acesso","dev_variaveis","dev_subagentes","dev_tools","dev_prompt_templates","dev_modelos_leitura"] as const).some(p => !!user?.permissions?.[p])) && (
            <NavItem to="/dev" icon={<IconPainelDev />} label="Painel de Dev" active={isActive("/dev")} />
          )}
          <NavItem to="/wiki" icon={<IconWiki />} label="Wiki" active={isActive("/wiki")} />
          <NavItem to="/settings/profile" icon={<IconSettings />} label="Configurações" active={isActive("/settings")} />
        </nav>

        {/* User footer */}
        <div style={{ padding: "12px 8px", borderTop: "1px solid var(--line)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 4px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1, padding: "4px 8px", color: "var(--ink-muted)" }}>
              <IconUser />
              <span style={{ fontSize: 13 }}>{user?.name ?? "—"}</span>
            </div>
            <button
              onClick={async () => {
                try { await logout(); } catch {}
                window.location.href = "/login";
              }}
              title="Sair"
              style={{
                background: "transparent",
                border: "1px solid var(--line)",
                borderRadius: 6,
                color: "var(--ink-dim)",
                fontSize: 11,
                padding: "4px 8px",
                cursor: "pointer",
              }}
              onMouseOver={e => (e.currentTarget.style.color = "var(--brand)")}
              onMouseOut={e => (e.currentTarget.style.color = "var(--ink-dim)")}
            >
              Sair
            </button>
          </div>
        </div>
      </aside>

      {/* ── Main ── */}
      <main
        style={{
          flex: 1,
          background: "var(--surface-app)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Outlet />
      </main>
      <ToastHost />
    </div>
  );
}
